import { useParams, Link } from "react-router-dom";
import { useMemo, useEffect, useState } from "react";
import PageLayout from "../../components/feature/PageLayout";
import ScrollReveal from "../../components/feature/ScrollReveal";
import { columns, columnCTA } from "../../mocks/columnData";
import NotFound from "../NotFound";

export default function ColumnDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = columns.find((c) => c.slug === slug);

  const [activeToc, setActiveToc] = useState<string>("");

  const relatedYokohama = columns.filter(
    (c) =>
      (c.categoryId === "date" || c.categoryId === "compare") &&
      c.slug !== (article?.slug ?? "")
  );
  const relatedGift = columns.filter(
    (c) =>
      (c.categoryId === "gift" || c.categoryId === "propose") &&
      c.slug !== (article?.slug ?? "")
  );
  const relatedSameCat = columns.filter(
    (c) => c.categoryId === article?.categoryId && c.slug !== (article?.slug ?? "")
  );

  const related = useMemo(() => {
    const pool = new Map<string, (typeof columns)[0]>();
    [...relatedYokohama, ...relatedGift, ...relatedSameCat].forEach((c) => {
      if (!pool.has(c.slug)) pool.set(c.slug, c);
    });
    return Array.from(pool.values()).slice(0, 4);
  }, [relatedYokohama, relatedGift, relatedSameCat]);

  useEffect(() => {
    if (!article) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveToc(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 }
    );
    article.toc.forEach((t) => {
      const el = document.getElementById(`toc-${t}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const ctaData =
    columnCTA[
      article.categoryId === "gift"
        ? "gift"
        : article.categoryId === "date" ||
          article.categoryId === "compare" ||
          article.categoryId === "propose"
        ? "yokohama"
        : "faq"
    ];

  return (
    <PageLayout>
      {/* Breadcrumb */}
      <nav className="bg-airdoa-cream">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto py-3 md:py-4">
            <ol className="flex items-center gap-2 text-xs text-airdoa-gray">
              <li>
                <Link to="/" className="hover:text-airdoa-navy transition-colors">
                  ホーム
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-airdoa-gray-light" />
              </li>
              <li>
                <Link to="/column" className="hover:text-airdoa-navy transition-colors">
                  コラム
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-airdoa-gray-light" />
              </li>
              <li className="text-airdoa-navy truncate max-w-[200px]">
                {article.title}
              </li>
            </ol>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
        <img
          src={article.heroImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-airdoa-navy/40 via-airdoa-navy/30 to-airdoa-navy/70" />

        <div className="relative z-10 flex flex-col items-center justify-end h-full text-center px-4 pb-8 md:pb-12">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase">
              {article.category}
            </span>
            <span className="text-[10px] text-white/60">{article.date}</span>
            {article.isNew && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium text-airdoa-navy bg-airdoa-gold rounded">
                NEW
              </span>
            )}
          </div>
          <h1 className="text-xl md:text-3xl lg:text-4xl font-serif font-bold text-white leading-tight max-w-3xl">
            {article.title}
          </h1>
          <div className="flex items-center gap-3 mt-3 text-[11px] text-white/70">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line" />
              </span>
              投稿 {article.date}
            </span>
            {article.updatedDate !== article.date && (
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-refresh-line" />
                </span>
                更新 {article.updatedDate}
              </span>
            )}
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-time-line" />
              </span>
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 md:py-16 bg-airdoa-white">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* TOC Sidebar */}
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-24 bg-airdoa-cream rounded-lg p-5">
                <p className="text-xs font-medium tracking-ultra text-airdoa-gold uppercase mb-4">
                  CONTENTS
                </p>
                <ul className="space-y-2.5">
                  {article.toc.map((t) => {
                    const id = `toc-${t}`;
                    const isActive = activeToc === id;
                    return (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          className={`block text-xs leading-relaxed transition-colors ${
                            isActive
                              ? "text-airdoa-navy font-semibold"
                              : "text-airdoa-gray hover:text-airdoa-navy"
                          }`}
                        >
                          {t}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </aside>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <ScrollReveal direction="up">
                {/* 導入文 */}
                <p className="text-base md:text-lg text-airdoa-navy leading-relaxed mb-8 font-medium">
                  {article.excerpt}
                </p>

                {/* Blocks */}
                <div className="flex flex-col gap-8">
                  {article.content.map((block, idx) => {
                    if (block.type === "heading") {
                      const id = `toc-${block.value}`;
                      return (
                        <h2
                          key={idx}
                          id={id}
                          className="text-lg md:text-xl font-serif font-semibold text-airdoa-navy leading-snug pt-4 scroll-mt-28"
                        >
                          {block.value}
                        </h2>
                      );
                    }
                    if (block.type === "paragraph") {
                      return (
                        <p
                          key={idx}
                          className="text-sm md:text-base text-airdoa-gray leading-[1.9]"
                        >
                          {block.value}
                        </p>
                      );
                    }
                    if (block.type === "image") {
                      return (
                        <div key={idx} className="my-2">
                          <img
                            src={block.value}
                            alt=""
                            className="w-full rounded-lg object-cover object-top"
                          />
                        </div>
                      );
                    }
                    if (block.type === "cta") {
                      const cta =
                        columnCTA[block.value as keyof typeof columnCTA];
                      if (!cta) return null;
                      return (
                        <div
                          key={idx}
                          className="my-4 bg-airdoa-cream rounded-lg p-6 md:p-8 border border-airdoa-gray-light/40"
                        >
                          <p className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase mb-2">
                            {cta.label}
                          </p>
                          <h3 className="text-lg md:text-xl font-serif font-semibold text-airdoa-navy mb-2">
                            {cta.title}
                          </h3>
                          <p className="text-sm text-airdoa-gray mb-5">
                            {cta.desc}
                          </p>
                          <Link
                            to={cta.href}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-airdoa-navy text-white text-sm font-medium rounded-md hover:bg-airdoa-navy-light transition-colors whitespace-nowrap"
                          >
                            {cta.button}
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-right-line" />
                            </span>
                          </Link>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </ScrollReveal>

              {/* まとめCTA */}
              <ScrollReveal direction="up" delay={200}>
                <div className="mt-12 bg-airdoa-navy rounded-lg p-6 md:p-8 text-center">
                  <p className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase mb-2">
                    {ctaData.label}
                  </p>
                  <h3 className="text-lg md:text-xl font-serif font-semibold text-white mb-2">
                    {ctaData.title}
                  </h3>
                  <p className="text-sm text-white/70 mb-5 max-w-xl mx-auto">
                    {ctaData.desc}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to={ctaData.href}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-airdoa-gold text-airdoa-navy text-sm font-semibold rounded-md hover:bg-airdoa-gold-dark transition-colors whitespace-nowrap"
                    >
                      {ctaData.button}
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </Link>
                    <Link
                      to="/faq"
                      className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white text-sm font-medium rounded-md hover:bg-white/10 transition-colors whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-questionnaire-line" />
                      </span>
                      FAQを見る
                    </Link>
                  </div>
                </div>
              </ScrollReveal>

              {/* Back to list */}
              <div className="mt-10 pt-6 border-t border-airdoa-gray-light/40 flex items-center gap-4">
                <Link
                  to="/column"
                  className="inline-flex items-center gap-2 text-sm text-airdoa-navy hover:text-airdoa-gold transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line" />
                  </span>
                  コラム一覧へ戻る
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 関連記事 */}
      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-cream">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <div className="text-center mb-8">
                  <p className="section-label mb-2">RELATED</p>
                  <h2 className="section-title">関連記事</h2>
                </div>
              </ScrollReveal>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((item, idx) => (
                  <ScrollReveal key={item.slug} direction="up" delay={idx * 80}>
                    <Link
                      to={`/column/${item.slug}`}
                      className="group block card-luxury h-full flex flex-col"
                    >
                      <div className="aspect-[3/2] overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase">
                            {item.category}
                          </span>
                          <span className="text-[10px] text-airdoa-gray">
                            {item.date}
                          </span>
                        </div>
                        <h3 className="text-sm font-serif font-semibold text-airdoa-navy leading-relaxed group-hover:text-airdoa-gold transition-colors duration-300">
                          {item.title}
                        </h3>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ 導線 */}
      <section className="py-16 md:py-20 bg-airdoa-white">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal direction="up">
              <p className="text-xs font-medium tracking-ultra text-airdoa-gold uppercase mb-2">
                FAQ
              </p>
              <h2 className="text-2xl md:text-3xl font-serif font-semibold text-airdoa-navy mb-3">
                まだ不安なことがありますか？
              </h2>
              <p className="text-sm md:text-base text-airdoa-gray mb-8 leading-relaxed">
                初めてのヘリ遊覧で気になることを、FAQで丁寧にお答えしています。服装や持ち物から、欠航時の対応まで、あなたの不安を解消します。
              </p>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 px-6 py-3 border border-airdoa-navy text-airdoa-navy text-sm font-medium rounded-md hover:bg-airdoa-navy hover:text-white transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-questionnaire-line" />
                </span>
                FAQを見る
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
